import { useState, useEffect, useRef, useId, useMemo } from "react";
import {
  Shield,
  Plus,
  Loader2,
  ArrowLeft,
  Cloud,
  Monitor,
  Mail,
  Globe,
  Key,
  X,
  Activity,
  Copy,
  Check,
  Terminal,
  RefreshCw,
  Building2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MacOsFdaCallout } from "@/components/ui/macos-fda-callout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCreateAgentKey,
  useCreateIntegration,
  useDeleteIntegration,
  useDeployAgents,
  useTestIntegration,
} from "@/hooks/use-api";
import type {
  AgentKeyInstall,
  AgentKeyPurpose,
  AgentOSType,
  CreateIntegrationRequest,
  IntegrationSourceType,
} from "@/types/api";

interface AddIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Skip the source picker when another page deep-links to a setup flow. */
  initialSourceType?: IntegrationSourceType | null;
}

interface SourceTypeMeta {
  key: IntegrationSourceType;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const SOURCE_TYPES: SourceTypeMeta[] = [
  {
    key: "entra_id",
    name: "Microsoft Entra ID",
    description: "Azure AD identity & sign-in logs",
    icon: Key,
  },
  {
    key: "crowdstrike",
    name: "CrowdStrike",
    description: "Endpoint detection & response",
    icon: Shield,
  },
  {
    key: "sentinel",
    name: "Microsoft Sentinel",
    description: "Azure SIEM alerts & incidents",
    icon: Shield,
  },
  {
    key: "m365_defender",
    name: "Microsoft 365 Defender",
    description: "Email & collaboration security",
    icon: Mail,
  },
  {
    key: "cloudtrail",
    name: "AWS CloudTrail",
    description: "AWS API activity logs",
    icon: Cloud,
  },
  {
    key: "azure_activity",
    name: "Azure Activity Log",
    description: "Azure resource management events",
    icon: Activity,
  },
  {
    key: "gcp_audit",
    name: "Google Cloud Audit Log",
    description: "Google Cloud Platform (IAM, resources) — not Workspace/Gmail",
    icon: Cloud,
  },
  {
    key: "google_workspace",
    name: "Google Workspace",
    description: "Admin console, logins, OAuth tokens (Admin SDK) — not Google Cloud",
    icon: Building2,
  },
  {
    key: "m365",
    name: "Microsoft 365",
    description: "Exchange & SharePoint audit logs",
    icon: Mail,
  },
  {
    key: "endpoint",
    name: "Endpoint Agent",
    description: "Linux, Windows & macOS endpoints",
    icon: Monitor,
  },
  {
    key: "firewall",
    name: "Firewall / Network",
    description: "Palo Alto, Fortinet, SonicWall, Cisco, Sophos",
    icon: Globe,
  },
  {
    key: "mimecast_email",
    name: "Mimecast Email Security",
    description: "Email security audit logs via Mimecast API 2.0",
    icon: Mail,
  },
];

// ponytail: MVP scope (Yosef 2026-06-24) — only validated source types get a
// create-tile. Every other type stays fully defined (fields/wizards intact) so
// existing integrations still render in the grid; un-hiding one = add its key
// here. No backend change — the API still accepts the hidden types.
const MVP_VISIBLE_SOURCE_TYPES = new Set<IntegrationSourceType>([
  "entra_id",
  "google_workspace",
  "endpoint",
]);
const VISIBLE_SOURCE_TYPES = SOURCE_TYPES.filter((s) => MVP_VISIBLE_SOURCE_TYPES.has(s.key));

interface FieldDef {
  key: string;
  label: string;
  placeholder?: string;
  type?: "text" | "password";
  /** HTML input pattern attribute (used for browser tooltip). */
  pattern?: string;
  /** Hint shown below the input. */
  hint?: string;
  /** If true, an empty value is accepted and the key is omitted from config. */
  optional?: boolean;
  /** Additional JS-side validation returning an error string, or null if valid. */
  validate?: (value: string) => string | null;
}

const SOURCE_FIELDS: Record<IntegrationSourceType, FieldDef[]> = {
  entra_id: [
    { key: "tenant_id", label: "Tenant ID", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
    { key: "client_id", label: "Client ID", placeholder: "Application (client) ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Client secret value" },
  ],
  crowdstrike: [
    { key: "client_id", label: "Client ID", placeholder: "CrowdStrike API client ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "CrowdStrike API client secret" },
    { key: "base_url", label: "Base URL", placeholder: "https://api.crowdstrike.com" },
  ],
  sentinel: [
    { key: "workspace_id", label: "Workspace ID", placeholder: "Log Analytics workspace ID" },
    { key: "tenant_id", label: "Tenant ID", placeholder: "Azure AD tenant ID" },
    { key: "client_id", label: "Client ID", placeholder: "Application (client) ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Client secret value" },
  ],
  m365_defender: [
    { key: "tenant_id", label: "Tenant ID", placeholder: "Azure AD tenant ID" },
    { key: "client_id", label: "Client ID", placeholder: "Application (client) ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Client secret value" },
  ],
  cloudtrail: [
    {
      key: "aws_account_id",
      label: "AWS Account ID",
      placeholder: "123456789012",
      hint: "12-digit AWS account ID",
      validate: (v) =>
        /^\d{12}$/.test(v) ? null : "Must be a 12-digit AWS account ID",
    },
    {
      key: "region",
      label: "Region",
      placeholder: "us-east-1",
      optional: true,
      hint: "Defaults to us-east-1 if not specified",
    },
  ],
  azure_activity: [
    { key: "tenant_id", label: "Tenant ID", placeholder: "Azure AD tenant ID" },
    { key: "client_id", label: "Client ID", placeholder: "Application (client) ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Client secret value" },
    { key: "subscription_id", label: "Subscription ID", placeholder: "Azure subscription ID" },
  ],
  m365: [
    { key: "tenant_id", label: "Tenant ID", placeholder: "Azure AD tenant ID" },
    { key: "client_id", label: "Client ID", placeholder: "Application (client) ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Client secret value" },
  ],
  gcp_audit: [
    { key: "project_id", label: "Project ID", placeholder: "my-gcp-project-123" },
    { key: "service_account_json", label: "Service Account Key (JSON)", placeholder: '{"type": "service_account", ...}' },
  ],
  // google_workspace uses a dedicated wizard (StepWorkspaceWizard), not the
  // generic field loop — keep the key present so the exhaustive Record stays
  // type-complete.
  google_workspace: [],
  mimecast_email: [
    { key: "client_id", label: "Client ID", placeholder: "Mimecast API application client ID" },
    { key: "client_secret", label: "Client Secret", type: "password", placeholder: "Mimecast API client secret" },
    { key: "base_url", label: "API Base URL", placeholder: "https://api.services.mimecast.com", optional: true, hint: "Override for regional endpoints, e.g. https://eu-api.mimecast.com" },
  ],
  endpoint: [],
  firewall: [
    { key: "expected_source_ips", label: "Expected Source IPs", placeholder: "10.0.0.1, 10.0.0.2 (comma separated)" },
  ],
};

// ── Google Workspace wizard helpers (#179-E) ────────────────

// Exact MVP scope the customer pastes into the Admin console DWD entry. Mirrors
// the backend's _GW_AUDIT_SCOPE; Directory/Usage scopes are intentionally not
// requested (no backend code consumes them yet).
const GW_AUDIT_SCOPE =
  "https://www.googleapis.com/auth/admin.reports.audit.readonly";

// Mirror the backend validators (src/api/routes/integrations.py) so the wizard
// rejects bad input before a round-trip — the server re-validates regardless.
const GW_EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const GW_CUSTOMER_ID_RE = /^(my_customer|C[A-Za-z0-9]+)$/;

const GW_APPLICATIONS: { key: string; label: string; help: string }[] = [
  { key: "admin", label: "Admin console", help: "User, role & security changes (recommended)" },
  { key: "login", label: "Login", help: "Sign-in successes & failures (recommended)" },
  { key: "token", label: "OAuth tokens", help: "Third-party app authorizations (recommended)" },
  { key: "rules", label: "Security rules", help: "Alert center & rule activity (recommended)" },
  { key: "drive", label: "Drive (admin)", help: "Sharing & ACL changes only — view/download noise excluded" },
  { key: "gmail", label: "Gmail (admin)", help: "Mailbox-security settings only — message content excluded" },
];
const GW_DEFAULT_APPS = ["admin", "login", "token", "rules"];

type SaParse =
  | { ok: true; clientId: string; clientEmail: string }
  | { ok: false; reason: string };

// Parse a pasted service-account JSON key entirely client-side, to (a) surface
// the numeric OAuth client ID the customer needs for the DWD entry and (b) gate
// submission. SECURITY: the key holds a private key — error reasons are fixed
// strings and NEVER echo the pasted content, so the secret can't leak into the
// DOM, console, or any error-reporting pipeline.
function parseServiceAccount(raw: string): SaParse {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, reason: "Paste the service-account JSON key." };
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch {
    return {
      ok: false,
      reason: "That isn't valid JSON. Paste the full service-account key file, including the braces.",
    };
  }
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return { ok: false, reason: "The JSON must be the service-account key object." };
  }
  const sa = obj as Record<string, unknown>;
  if (sa.type !== "service_account") {
    return { ok: false, reason: 'This is not a service-account key — its "type" must be "service_account".' };
  }
  const clientEmail = typeof sa.client_email === "string" ? sa.client_email : "";
  if (!clientEmail) return { ok: false, reason: "The key is missing client_email." };
  // Require client_id to be a digit STRING. Google emits it quoted; an unquoted
  // JSON number (21 digits) would already have lost precision through JSON.parse,
  // so we'd display/copy the wrong DWD client ID — reject rather than guess.
  const clientId = typeof sa.client_id === "string" ? sa.client_id : "";
  if (!/^\d+$/.test(clientId)) {
    return {
      ok: false,
      reason:
        "Use the numeric OAuth client ID from the key (the quoted client_id field, digits only) — not the service-account email.",
    };
  }
  if (typeof sa.private_key !== "string" || !sa.private_key) {
    return { ok: false, reason: "The key is missing private_key." };
  }
  if (typeof sa.private_key_id !== "string" || !sa.private_key_id) {
    return { ok: false, reason: "The key is missing private_key_id." };
  }
  return { ok: true, clientId, clientEmail };
}

// ── Endpoint wizard helpers (#155) ──────────────────────────

// Format the install token's `expires_at` ISO string for display in
// the success panel. Uses a relative phrasing ("expires in N minutes")
// for the typical-case 10-minute TTL the customer is about to paste,
// and an absolute UTC string for the longer-tail case so a skewed
// browser clock doesn't make the message wrong by hours.
function formatExpiresAt(iso: string): string {
  const exp = new Date(iso);
  const ms = exp.getTime() - Date.now();
  if (Number.isNaN(ms)) return "Single-use";
  if (ms <= 0) return "Single-use; this URL has expired — regenerate";
  if (ms < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.round(ms / 60000));
    return `Single-use; expires in ${mins} minute${mins === 1 ? "" : "s"}`;
  }
  // For >1h cases (rarely reached for a 10-min TTL, but possible if
  // the dialog sits open after a fresh mint), prefer an absolute UTC
  // string so a clock-skewed browser doesn't show a wrong relative time.
  const formatted = exp.toISOString().replace("T", " ").slice(0, 16);
  return `Single-use; expires ${formatted} UTC`;
}

// Build the OS-specific one-liner that pipes the install URL through
// bash / PowerShell. The URL itself is opaque to the frontend — it's an
// HMAC-signed single-use token from POST /api/v1/agent/keys. We just
// wrap it in the right shell invocation per platform.
function buildOneLiner(os: AgentOSType, installUrl: string): string {
  switch (os) {
    case "linux":
    case "macos":
      // curl|bash. -fsSL fails fast on HTTP errors so a 410 (token
      // spent / expired) doesn't silently render an empty script. sudo
      // because the installer touches systemd / launchd / /etc paths.
      return `curl -fsSL "${installUrl}" | sudo bash`;
    case "windows":
      // PowerShell with the URL in single quotes (no expansion) so a
      // future regression that appends a `$signature=` query string
      // can't be variable-substituted. iwr handles text/plain + UTF-8
      // gracefully on Windows 10/11 + Server. Explicit ExecutionPolicy
      // bypass for the current process so a default-locked machine
      // doesn't error out.
      return `powershell -NoProfile -ExecutionPolicy Bypass -NoExit -Command "\`$ErrorActionPreference='Stop'; try { iwr -useb '${installUrl}' | iex } catch { Write-Host ''; Write-Error \`$_ -ErrorAction Continue; Read-Host 'Press Enter to close'; exit 1 }"`;
    default: {
      // Exhaustive guard: if `AgentOSType` ever gains a value, this
      // throws at runtime AND fails type-checking on the next build,
      // forcing the new branch to be added explicitly.
      const _exhaustive: never = os;
      throw new Error(`unsupported os: ${String(_exhaustive)}`);
    }
  }
}

// ── Dialog ──────────────────────────────────────────────────

export function AddIntegrationDialog({
  open,
  onOpenChange,
  initialSourceType = null,
}: AddIntegrationDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedType, setSelectedType] = useState<SourceTypeMeta | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  // Endpoint-flow wizard state. When the user picks "Endpoint Agent" at
  // step 1, step 2 collects OS / Purpose / instance label and step 3
  // shows the rendered install URL + waits for the first heartbeat.
  const [endpointOs, setEndpointOs] = useState<AgentOSType>("linux");
  const [endpointPurpose, setEndpointPurpose] = useState<AgentKeyPurpose>("prod");
  const [instanceLabel, setInstanceLabel] = useState("");
  // Filled on successful POST /api/v1/agent/keys with `os` set. The
  // install_url is the customer-facing artifact; agent_key_id is held
  // for the heartbeat poll.
  const [installInfo, setInstallInfo] = useState<AgentKeyInstall | null>(null);
  // The agent key id from the mint response. We don't display it but
  // hold it so a future "Cancel install" affordance can revoke without
  // round-tripping back to the agent-keys list.
  const [, setInstallAgentKeyId] = useState<string | null>(null);

  // Google Workspace wizard state (#179-E). The pasted SA JSON lives here only
  // while step 2 is open and is sent in the create payload; reset() clears it.
  const [gwSaJson, setGwSaJson] = useState("");
  const [gwAdminEmail, setGwAdminEmail] = useState("");
  const [gwCustomerId, setGwCustomerId] = useState("my_customer");
  const [gwApps, setGwApps] = useState<string[]>(GW_DEFAULT_APPS);
  // Set on a successful Workspace create so the step-3 confirm panel can run the
  // async-probe connectivity test against the new integration.
  const [gwIntegrationId, setGwIntegrationId] = useState<string | null>(null);

  const createMutation = useCreateIntegration();
  const createKeyMutation = useCreateAgentKey();
  const deleteIntegrationMutation = useDeleteIntegration();
  const { data: agentsData, refetch: refetchAgents } = useDeployAgents({ enabled: step === 3 });

  const isEndpointSetup = createMutation.isPending || createKeyMutation.isPending;

  // Synchronous guard against duplicate regenerate clicks — mirrors the
  // fetchedRef pattern in InstallCommandTabs. createKeyMutation.isPending
  // disables the button after React re-renders, but two rapid activations
  // can both land before the render commits. Reset on success/failure.
  const regenerateInFlightRef = useRef(false);
  // Monotonically incrementing session counter. Captured before an async
  // mutation and compared after to detect close/reopen races: if the dialog
  // closes and reopens while a create/regenerate is in flight, stale results
  // would overwrite fresh state without this guard. Both handleCreate and
  // handleRegenerate capture and compare this counter around every await.
  const sessionRef = useRef(0);

  // Whether we entered the 5-minute heartbeat-poll cap on step 3. When
  // true, the panel switches from "Waiting…" to "Stopped checking —
  // click Refresh" so the customer knows the auto-poll has stopped.
  const [pollCapReached, setPollCapReached] = useState(false);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        handleClose();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onOpenChange]);

  // Poll agents every 5s on step 3, stop after 5 minutes. Surface the
  // stop point via `pollCapReached` so the panel can swap "Waiting…" for
  // "Stopped checking — click Refresh" rather than appearing frozen.
  // installInfo?.token is in deps so a successful regenerate restarts the
  // poll window from zero (the effect cleanup tears down the old interval
  // and the new effect body sets a fresh pollStartRef.current).
  const pollStartRef = useRef<number | null>(null);
  const installToken = installInfo?.token;
  useEffect(() => {
    if (!open || !initialSourceType || selectedType || step !== 1) return;
    const source = SOURCE_TYPES.find((candidate) => candidate.key === initialSourceType);
    if (!source) return;
    setSelectedType(source);
    setDisplayName(source.name);
    setFieldValues({});
    setError(null);
    setStep(2);
  }, [initialSourceType, open, selectedType, step]);

  useEffect(() => {
    if (step !== 3) {
      pollStartRef.current = null;
      setPollCapReached(false);
      return;
    }
    if (!pollStartRef.current) pollStartRef.current = Date.now();
    const start = pollStartRef.current;
    const id = setInterval(() => {
      if (Date.now() - start > 5 * 60 * 1000) {
        clearInterval(id);
        setPollCapReached(true);
        return;
      }
      void refetchAgents();
    }, 5000);
    return () => clearInterval(id);
  }, [step, refetchAgents, installToken]);

  function reset() {
    sessionRef.current++;
    regenerateInFlightRef.current = false;
    setStep(1);
    setSelectedType(null);
    setDisplayName("");
    setFieldValues({});
    setError(null);
    setEndpointOs("linux");
    setEndpointPurpose("prod");
    setInstanceLabel("");
    setInstallInfo(null);
    setInstallAgentKeyId(null);
    setPollCapReached(false);
    pollStartRef.current = null;
    setGwSaJson("");
    setGwAdminEmail("");
    setGwCustomerId("my_customer");
    setGwApps(GW_DEFAULT_APPS);
    setGwIntegrationId(null);
  }

  async function handleRegenerate() {
    if (!selectedType || !installInfo) return;
    if (regenerateInFlightRef.current) return;
    regenerateInFlightRef.current = true;
    // Capture session before awaiting so a close→reopen race is detectable:
    // session increments on reset(), so a stale result sees a mismatched
    // session and is dropped.
    const session = sessionRef.current;
    setError(null);
    try {
      // Use the server-confirmed instance_label from the current install so
      // auto-generated labels are preserved across regenerations (if the
      // operator left instance_label blank initially, re-submitting blank
      // would produce a different auto-generated label).
      const keyResult = await createKeyMutation.mutateAsync({
        name: `${displayName.trim() || selectedType.name} Key`,
        purpose: endpointPurpose,
        os: endpointOs,
        instance_label: installInfo.instance_label,
      });
      if (session !== sessionRef.current) return;
      if (!keyResult.install) {
        setError(
          "Install URL was not returned by the backend. Try again or contact support.",
        );
        return;
      }
      setInstallInfo(keyResult.install);
      setInstallAgentKeyId(keyResult.key.id);
      // Fresh token — reset polling so the new install's agent connection
      // is watched from the start (avoids a stuck "Stopped checking" banner
      // if the user hit the 5-minute cap before regenerating).
      setPollCapReached(false);
      pollStartRef.current = null;
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError(
        err instanceof Error ? err.message : "Failed to regenerate install token",
      );
    } finally {
      // Only clear the in-flight guard if this invocation still owns the
      // current session. A stale invocation that raced across close→reopen
      // must not clear the guard that the new session's in-flight call set.
      if (session === sessionRef.current) {
        regenerateInFlightRef.current = false;
      }
    }
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  function handleSelectType(source: SourceTypeMeta) {
    setSelectedType(source);
    setDisplayName(source.name);
    setFieldValues({});
    setError(null);
    setStep(2);
  }

  function handleBack() {
    if (step === 3) {
      // From deploy step, just close (integration already created)
      handleClose();
      return;
    }
    setStep(1);
    setError(null);
  }

  function handleFieldChange(key: string, value: string) {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCreate() {
    if (!selectedType) return;

    // Google Workspace has a dedicated wizard + payload shape — handle it before
    // the generic field loop and move to the step-3 connectivity-confirm panel.
    if (selectedType.key === "google_workspace") {
      const sa = parseServiceAccount(gwSaJson);
      if (!sa.ok) {
        setError(sa.reason);
        return;
      }
      const adminEmail = gwAdminEmail.trim();
      if (!GW_EMAIL_RE.test(adminEmail)) {
        setError("Enter a valid super-admin email address to impersonate.");
        return;
      }
      const customerId = gwCustomerId.trim() || "my_customer";
      if (!GW_CUSTOMER_ID_RE.test(customerId)) {
        setError('Customer ID must be "my_customer" or a Google customer ID like C03az79cb.');
        return;
      }
      if (gwApps.length === 0) {
        setError("Select at least one activity feed to collect.");
        return;
      }
      const payload: CreateIntegrationRequest = {
        source_type: "google_workspace",
        display_name: displayName.trim() || selectedType.name,
        config: {
          // Sent as the raw pasted string; the backend accepts string or object
          // and stores it in Secrets Manager (never in config JSONB).
          service_account_json: gwSaJson.trim(),
          admin_email: adminEmail,
          customer_id: customerId,
          applications: gwApps,
        },
      };
      setError(null);
      const session = sessionRef.current;
      try {
        const result = await createMutation.mutateAsync(payload);
        if (session !== sessionRef.current) return;
        // Drop the pasted private key from state the moment it's been submitted —
        // the confirm panel never needs it, so it shouldn't linger in memory.
        setGwSaJson("");
        setGwIntegrationId(result?.integration?.id ?? null);
        setStep(3);
      } catch (err) {
        if (session !== sessionRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to create integration");
      }
      return;
    }

    const fields = SOURCE_FIELDS[selectedType.key];
    const config: Record<string, unknown> = {};

    for (const field of fields) {
      const value = fieldValues[field.key]?.trim();
      if (!value) {
        if (field.optional) continue; // omit optional empty fields from config
        setError(`${field.label} is required`);
        return;
      }
      if (field.validate) {
        const err = field.validate(value);
        if (err) {
          setError(err);
          return;
        }
      }
      if (field.key === "expected_source_ips") {
        config[field.key] = value.split(",").map((s) => s.trim()).filter(Boolean);
      } else {
        config[field.key] = value;
      }
    }

    const payload: CreateIntegrationRequest = {
      source_type: selectedType.key,
      display_name: displayName.trim() || selectedType.name,
      config,
    };

    setError(null);
    // Capture session before any await so a close→reopen race is detectable.
    // session increments on reset(), so a stale result sees a mismatched
    // session and is dropped.
    const session = sessionRef.current;

    try {
      const integrationResult = await createMutation.mutateAsync(payload);

      // Drop stale results from a previous session (close→reopen race).
      if (session !== sessionRef.current) return;

      if (selectedType.key === "endpoint") {
        // Mint key + install token in one call. Failure rolls back the
        // freshly-created integration so we don't leave an orphan that
        // the customer can't get an install URL for.
        const integrationId = integrationResult?.integration?.id;
        try {
          const trimmedLabel = instanceLabel.trim();
          const keyResult = await createKeyMutation.mutateAsync({
            name: `${displayName.trim() || selectedType.name} Key`,
            purpose: endpointPurpose,
            os: endpointOs,
            ...(trimmedLabel ? { instance_label: trimmedLabel } : {}),
          });
          if (session !== sessionRef.current) return;
          if (!keyResult.install) {
            // Backend should always return `install` when `os` is in
            // the request — defend against future shape drift so the
            // customer sees a clear error rather than a silent empty
            // step.
            setError(
              "Install URL was not returned by the backend. Try again or contact support.",
            );
            // No install URL means the orphan revoke logic in
            // _mint_install_extras did NOT run — the agent key exists
            // server-side but is unreachable. Roll back the integration
            // too so the customer has a clean slate.
            if (integrationId && integrationResult.created !== false) {
              try {
                await deleteIntegrationMutation.mutateAsync(integrationId);
              } catch {
                // Best-effort cleanup; the customer can delete from
                // the Integrations page if this fails too.
              }
            }
          } else {
            setInstallInfo(keyResult.install);
            setInstallAgentKeyId(keyResult.key.id);
          }
        } catch (err) {
          if (session !== sessionRef.current) return;
          const message =
            err instanceof Error ? err.message : "Failed to mint install URL";
          // Mint failed — the integration exists but the key flow is
          // broken. Best-effort delete the integration so the customer
          // doesn't end up with a row in /integrations they can't
          // actually use. If the cleanup itself fails, augment the
          // visible error so the customer knows to remove the row
          // manually.
          let cleanupFailed = false;
          if (integrationId && integrationResult.created !== false) {
            try {
              await deleteIntegrationMutation.mutateAsync(integrationId);
            } catch {
              cleanupFailed = true;
            }
          }
          if (session !== sessionRef.current) return;
          setError(
            cleanupFailed
              ? `${message} — visit Integrations to remove the half-created row.`
              : message,
          );
        }
        if (session !== sessionRef.current) return;
        setStep(3);
      } else {
        handleClose();
      }
    } catch (err) {
      if (session !== sessionRef.current) return;
      const message =
        err instanceof Error ? err.message : "Failed to create integration";
      setError(message);
    }
  }

  if (!open) return null;

  const stepTitle =
    step === 1
      ? "Add Integration"
      : step === 3
        ? selectedType?.key === "google_workspace"
          ? "Google Workspace connected"
          : "Deploy Agent"
        : `Configure ${selectedType?.name}`;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={stepTitle}
          className="pointer-events-auto relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-2">
              {(step === 2 || step === 3) && (
                <button
                  className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
                  onClick={handleBack}
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h2 className="text-lg font-semibold">{stepTitle}</h2>
            </div>
            <button
              className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
              onClick={handleClose}
              aria-label="Close dialog"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Body — scrolls within the viewport-capped panel so tall wizards
              (e.g. Google Workspace) never bleed off the top/bottom of the screen */}
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {step === 1 ? (
              <StepSelectType onSelect={handleSelectType} />
            ) : step === 2 && selectedType?.key === "google_workspace" ? (
              <StepWorkspaceWizard
                displayName={displayName}
                onDisplayNameChange={setDisplayName}
                saJson={gwSaJson}
                onSaJsonChange={setGwSaJson}
                adminEmail={gwAdminEmail}
                onAdminEmailChange={setGwAdminEmail}
                customerId={gwCustomerId}
                onCustomerIdChange={setGwCustomerId}
                apps={gwApps}
                onAppsChange={setGwApps}
                error={error}
                isSubmitting={createMutation.isPending}
                onSubmit={handleCreate}
              />
            ) : step === 2 && selectedType?.key === "endpoint" ? (
              <StepEndpointWizard
                displayName={displayName}
                onDisplayNameChange={setDisplayName}
                os={endpointOs}
                onOsChange={setEndpointOs}
                purpose={endpointPurpose}
                onPurposeChange={setEndpointPurpose}
                instanceLabel={instanceLabel}
                onInstanceLabelChange={setInstanceLabel}
                error={error}
                isSubmitting={isEndpointSetup}
                onSubmit={handleCreate}
              />
            ) : step === 2 && selectedType ? (
              <StepConfigure
                source={selectedType}
                fields={SOURCE_FIELDS[selectedType.key]}
                displayName={displayName}
                onDisplayNameChange={setDisplayName}
                fieldValues={fieldValues}
                onFieldChange={handleFieldChange}
                error={error}
                isSubmitting={createMutation.isPending}
                onSubmit={handleCreate}
              />
            ) : step === 3 && selectedType?.key === "google_workspace" ? (
              <StepWorkspaceConfirm
                displayName={displayName.trim() || "Google Workspace"}
                integrationId={gwIntegrationId}
                onDone={handleClose}
              />
            ) : step === 3 ? (
              <StepEndpointDeploy
                install={installInfo}
                os={endpointOs}
                error={error}
                agentCount={agentsData?.summary.active ?? 0}
                pollCapReached={pollCapReached}
                onRefreshAgents={() => void refetchAgents()}
                onDone={handleClose}
                onRegenerate={handleRegenerate}
                isRegenerating={createKeyMutation.isPending}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

function StepSelectType({
  onSelect,
}: {
  onSelect: (source: SourceTypeMeta) => void;
}) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Select the type of integration to configure.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {VISIBLE_SOURCE_TYPES.map((source) => {
          const Icon = source.icon;
          return (
            <button
              key={source.key}
              className="text-left"
              onClick={() => onSelect(source)}
            >
              <Card className="transition-colors hover:border-primary/50 hover:bg-accent/50 cursor-pointer">
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{source.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {source.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepConfigure({
  source,
  fields,
  displayName,
  onDisplayNameChange,
  fieldValues,
  onFieldChange,
  error,
  isSubmitting,
  onSubmit,
}: {
  source: SourceTypeMeta;
  fields: FieldDef[];
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* Display Name */}
      <div>
        <label className="text-sm font-medium" htmlFor="display-name">
          Display Name
        </label>
        <input
          id="display-name"
          type="text"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder={source.name}
          className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      {/* Config fields */}
      {fields.map((field) => (
        <div key={field.key}>
          <label className="text-sm font-medium" htmlFor={field.key}>
            {field.label}
            {field.optional && (
              <span className="ml-1 font-normal text-muted-foreground text-xs">
                (optional)
              </span>
            )}
          </label>
          <input
            id={field.key}
            type={field.type ?? "text"}
            value={fieldValues[field.key] ?? ""}
            onChange={(e) => onFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            pattern={field.pattern}
            title={field.hint}
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          {field.hint && (
            <p className="mt-1 text-xs text-muted-foreground">{field.hint}</p>
          )}
        </div>
      ))}

      {fields.length === 0 && (
        <p className="rounded-md bg-muted/50 px-3 py-3 text-sm text-muted-foreground">
          No additional configuration required. Endpoint agents self-register
          using an agent key after creation.
        </p>
      )}

      {/* Error */}
      {error && (
        <div id="integration-form-error" role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end pt-2">
        <Button onClick={onSubmit} disabled={isSubmitting} aria-describedby={error ? "integration-form-error" : undefined}>
          {isSubmitting && (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Create Integration
        </Button>
      </div>
    </div>
  );
}

function CopyBtn({ text, ariaLabel }: { text: string; ariaLabel?: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  // Keep the descriptor in the copied-state label too, so two adjacent copy
  // buttons stay distinguishable to assistive tech after activation.
  const idleLabel = ariaLabel ?? "Copy";
  const copiedLabel = ariaLabel ? ariaLabel.replace(/^Copy\b/i, "Copied") : "Copied";
  function handleCopy() {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setCopyFailed(false);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        // Surface a brief inline hint so the customer doesn't think the
        // button worked when it silently failed (e.g. browser policy
        // blocked clipboard access on insecure origin / iframe).
        console.warn("Clipboard copy failed:", err);
        setCopyFailed(true);
        setTimeout(() => setCopyFailed(false), 3000);
      });
  }
  return (
    <span className="inline-flex items-center gap-2">
      {copyFailed && (
        <span className="text-xs text-amber-500" role="status">
          Copy failed &mdash; select &amp; copy manually
        </span>
      )}
      <button
        className="rounded-sm p-1.5 text-muted-foreground hover:text-foreground transition-colors"
        onClick={handleCopy}
        aria-label={copied ? copiedLabel : idleLabel}
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500" aria-hidden="true" />
        ) : (
          <Copy className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </span>
  );
}

// ── Google Workspace wizard step 2 (#179-E) ─────────────────

const gwInputClass =
  "mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function StepWorkspaceWizard({
  displayName,
  onDisplayNameChange,
  saJson,
  onSaJsonChange,
  adminEmail,
  onAdminEmailChange,
  customerId,
  onCustomerIdChange,
  apps,
  onAppsChange,
  error,
  isSubmitting,
  onSubmit,
}: {
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  saJson: string;
  onSaJsonChange: (value: string) => void;
  adminEmail: string;
  onAdminEmailChange: (value: string) => void;
  customerId: string;
  onCustomerIdChange: (value: string) => void;
  apps: string[];
  onAppsChange: (value: string[]) => void;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  // Live client-side parse for the DWD client-ID display. Only flag a parse
  // problem once the customer has typed something, so the field doesn't shout
  // "invalid" before they've pasted.
  const sa = useMemo(() => parseServiceAccount(saJson), [saJson]);
  const showSaError = saJson.trim().length > 0 && !sa.ok;

  function toggleApp(key: string) {
    onAppsChange(apps.includes(key) ? apps.filter((a) => a !== key) : [...apps, key]);
  }

  return (
    <div className="space-y-5">
      {/* Distinction callout — the whole point of a dedicated wizard. */}
      <div className="rounded-md border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
        <p className="font-medium">This is Google Workspace, not Google Cloud.</p>
        <p className="mt-1 text-muted-foreground">
          It reads Admin SDK Reports audit events (Admin console, logins, OAuth
          tokens, security rules) via a service account with domain-wide
          delegation. For Google Cloud Platform IAM/resource logs, use the{" "}
          <span className="font-medium">Google Cloud Audit Log</span>{" "}
          integration instead.
        </p>
      </div>

      {/* Setup steps */}
      <ol className="space-y-2 text-sm text-muted-foreground">
        {[
          "In Google Cloud Console, create a project, add a service account, and download its JSON key.",
          "Enable the Admin SDK API in that project (APIs & Services → Library).",
          "On the service account, enable domain-wide delegation (Service account → Details → Advanced settings).",
          "Paste the JSON key below — we'll show the numeric client ID you need next.",
          "In Admin console → Security → API controls → Domain-wide delegation, add that client ID with the exact scope shown below.",
        ].map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold mt-0.5">
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>

      {/* Display Name */}
      <div>
        <label className="text-sm font-medium" htmlFor="display-name">
          Display Name
        </label>
        <input
          id="display-name"
          type="text"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder="Google Workspace"
          className={gwInputClass}
        />
      </div>

      {/* Service-account JSON */}
      <div>
        <label className="text-sm font-medium" htmlFor="gw-sa-json">
          Service Account Key (JSON)
        </label>
        <textarea
          id="gw-sa-json"
          value={saJson}
          onChange={(e) => onSaJsonChange(e.target.value)}
          placeholder='{"type": "service_account", "client_id": "1234567890", ...}'
          rows={5}
          spellCheck={false}
          aria-describedby="gw-sa-json-hint"
          className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <p id="gw-sa-json-hint" className="mt-1 text-xs text-muted-foreground">
          Parsed in your browser only. The private key is sent once to create the
          integration and stored encrypted — it is never logged or echoed back.
        </p>
        {showSaError && !sa.ok && (
          <p role="alert" className="mt-1.5 text-xs text-red-400">
            {sa.reason}
          </p>
        )}
      </div>

      {/* DWD guidance: numeric client ID + exact scope, shown once the key parses */}
      {sa.ok && (
        <div
          data-testid="gw-dwd-guidance"
          className="rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm"
        >
          <p className="font-medium text-green-400">
            Service account detected: {sa.clientEmail}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Add this client ID in Admin console → Security → API controls →
            Domain-wide delegation:
          </p>
          <div className="mt-1 flex items-center justify-between gap-2 rounded border bg-background px-3 py-1.5">
            <code data-testid="gw-client-id" className="font-mono text-sm">
              {sa.clientId}
            </code>
            <CopyBtn text={sa.clientId} ariaLabel="Copy OAuth client ID" />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">With this exact OAuth scope:</p>
          <div className="mt-1 flex items-center justify-between gap-2 rounded border bg-background px-3 py-1.5">
            <code className="overflow-x-auto font-mono text-xs">{GW_AUDIT_SCOPE}</code>
            <CopyBtn text={GW_AUDIT_SCOPE} ariaLabel="Copy OAuth scope" />
          </div>
        </div>
      )}

      {/* admin_email */}
      <div>
        <label className="text-sm font-medium" htmlFor="gw-admin-email">
          Super-admin email
        </label>
        <input
          id="gw-admin-email"
          type="email"
          value={adminEmail}
          onChange={(e) => onAdminEmailChange(e.target.value)}
          placeholder="admin@your-domain.com"
          aria-describedby="gw-admin-email-hint"
          className={gwInputClass}
        />
        <p id="gw-admin-email-hint" className="mt-1 text-xs text-muted-foreground">
          An active super-admin to impersonate. Suspended or non-admin users
          can't be impersonated.
        </p>
      </div>

      {/* customer_id */}
      <div>
        <label className="text-sm font-medium" htmlFor="gw-customer-id">
          Customer ID
        </label>
        <input
          id="gw-customer-id"
          type="text"
          value={customerId}
          onChange={(e) => onCustomerIdChange(e.target.value)}
          placeholder="my_customer"
          aria-describedby="gw-customer-id-hint"
          className={gwInputClass}
        />
        <p id="gw-customer-id-hint" className="mt-1 text-xs text-muted-foreground">
          Leave as <code className="font-mono">my_customer</code> for your own
          tenant, or enter a Google customer ID like{" "}
          <code className="font-mono">C03az79cb</code>.
        </p>
      </div>

      {/* applications */}
      <div>
        <p className="text-sm font-medium">Activity feeds</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Select which Admin SDK Reports feeds to collect.
        </p>
        <div className="mt-2 space-y-2">
          {GW_APPLICATIONS.map((app) => {
            const checked = apps.includes(app.key);
            const id = `gw-app-${app.key}`;
            return (
              <div
                key={app.key}
                className={`flex items-start gap-3 rounded-md border px-3 py-2 transition-colors ${
                  checked ? "border-primary bg-primary/5" : "border-input hover:border-primary/40"
                }`}
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleApp(app.key)}
                  className="mt-0.5 h-4 w-4"
                />
                <label htmlFor={id} className="cursor-pointer">
                  <span className="text-sm font-medium">{app.label}</span>
                  <span className="block text-xs text-muted-foreground">{app.help}</span>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          id="integration-form-error"
          role="alert"
          className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400"
        >
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          aria-describedby={error ? "integration-form-error" : undefined}
        >
          {isSubmitting && (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Create Integration
        </Button>
      </div>
    </div>
  );
}

// ── Google Workspace wizard step 3 (#179-E) ─────────────────

// Post-create confirmation. The connectivity probe is async (runs out-of-VPC in
// the ingestion Lambda), so there's nothing to show synchronously — we surface
// the queued state and let the customer run the test on demand. The backend
// probe already returns actionable, secret-free detail strings (DWD scope,
// Admin SDK not enabled, non-super-admin, …); we display those verbatim rather
// than maintaining a parallel error-mapping table that could drift.
function StepWorkspaceConfirm({
  displayName,
  integrationId,
  onDone,
}: {
  displayName: string;
  integrationId: string | null;
  onDone: () => void;
}) {
  const testMutation = useTestIntegration();
  const [result, setResult] = useState<
    { state: "verified" | "pending" | "error"; detail: string } | null
  >(null);

  async function handleTest() {
    if (!integrationId) return;
    setResult(null);
    try {
      const res = await testMutation.mutateAsync(integrationId);
      const check =
        res.checks.find((c) => c.name === "google_workspace_connectivity") ??
        res.checks.find((c) => !c.passed) ??
        res.checks[0];
      const detail = check?.detail ?? "No connectivity detail returned.";
      if (check?.passed) {
        setResult({ state: "verified", detail });
      } else if (/status:\s*(queued|running)/i.test(detail)) {
        // Probe hasn't landed yet — not an error.
        setResult({
          state: "pending",
          detail: "Probe queued — the first poll can take up to 10 minutes. Check again shortly.",
        });
      } else {
        setResult({ state: "error", detail });
      }
    } catch (err) {
      setResult({
        state: "error",
        detail: err instanceof Error ? err.message : "Failed to test connectivity.",
      });
    }
  }

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3">
        <p className="text-sm font-medium text-green-400">
          {displayName} created.
        </p>
        <p className="mt-1 text-xs text-green-400/80">
          We're verifying connectivity in the background. The first poll can take
          up to 10 minutes — the Integrations page shows live health, or check now
          below.
        </p>
      </div>

      {result && (
        <div
          role={result.state === "error" ? "alert" : "status"}
          data-testid="gw-test-result"
          className={`rounded-md border px-4 py-3 text-sm ${
            result.state === "verified"
              ? "border-green-500/30 bg-green-500/10 text-green-400"
              : result.state === "pending"
                ? "border-amber-500/30 bg-amber-500/10 text-amber-400"
                : "border-red-500/30 bg-red-500/10 text-red-400"
          }`}
        >
          {result.state === "verified" ? "Connectivity verified. " : ""}
          {result.detail}
        </div>
      )}

      <div className="flex items-center justify-between border-t pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={!integrationId || testMutation.isPending}
          data-testid="gw-test-connection"
        >
          {testMutation.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" />
          )}
          Check connection
        </Button>
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}

// ── Endpoint wizard step 2 (#155) ───────────────────────────

const PURPOSE_OPTIONS: { key: AgentKeyPurpose; label: string; help: string }[] = [
  {
    key: "prod",
    label: "Production",
    help: "Long-lived deployment. Never auto-revoked.",
  },
  {
    key: "test",
    label: "Test",
    help: "Auto-revokes after 14 days idle (#82).",
  },
  {
    key: "demo",
    label: "Demo",
    help: "Auto-revokes after 14 days idle.",
  },
];

const OS_OPTIONS: { key: AgentOSType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: "linux", label: "Linux", icon: Terminal },
  { key: "macos", label: "macOS", icon: Monitor },
  { key: "windows", label: "Windows", icon: Monitor },
];

function StepEndpointWizard({
  displayName,
  onDisplayNameChange,
  os,
  onOsChange,
  purpose,
  onPurposeChange,
  instanceLabel,
  onInstanceLabelChange,
  error,
  isSubmitting,
  onSubmit,
}: {
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  os: AgentOSType;
  onOsChange: (value: AgentOSType) => void;
  purpose: AgentKeyPurpose;
  onPurposeChange: (value: AgentKeyPurpose) => void;
  instanceLabel: string;
  onInstanceLabelChange: (value: string) => void;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Display Name */}
      <div>
        <label className="text-sm font-medium" htmlFor="display-name">
          Display Name
        </label>
        <input
          id="display-name"
          type="text"
          value={displayName}
          onChange={(e) => onDisplayNameChange(e.target.value)}
          placeholder="Endpoint Agent"
          className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      {/* OS picker */}
      <div>
        <p className="text-sm font-medium">Operating System</p>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {OS_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const selected = os === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onOsChange(opt.key)}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  selected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-input bg-background text-muted-foreground hover:border-primary/40"
                }`}
                aria-pressed={selected}
              >
                <Icon className="h-4 w-4" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Purpose picker */}
      <div>
        <p className="text-sm font-medium">Purpose</p>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {PURPOSE_OPTIONS.map((opt) => {
            const selected = purpose === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onPurposeChange(opt.key)}
                className={`rounded-md border px-3 py-2 text-left transition-colors ${
                  selected
                    ? "border-primary bg-primary/10"
                    : "border-input bg-background hover:border-primary/40"
                }`}
                aria-pressed={selected}
              >
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.help}</p>
              </button>
            );
          })}
        </div>
        {(purpose === "test" || purpose === "demo") && (
          <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
            Test &amp; Demo agent keys auto-expire after 14 days idle. Use
            Production for long-lived deployments.
          </p>
        )}
      </div>

      {/* Optional instance label */}
      <div>
        <label className="text-sm font-medium" htmlFor="instance-label">
          Instance label{" "}
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </label>
        <input
          id="instance-label"
          type="text"
          value={instanceLabel}
          onChange={(e) => onInstanceLabelChange(e.target.value)}
          placeholder={`${purpose}-${os}-...`}
          maxLength={128}
          aria-describedby="instance-label-hint"
          className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <p id="instance-label-hint" className="mt-1 text-xs text-muted-foreground">
          Shown in the agents dashboard so you can tell which host is which.
          Leave blank to auto-generate. Up to 128 characters.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400"
        >
          {error}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting && (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          <Key className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Generate Install Command
        </Button>
      </div>
    </div>
  );
}


// ── Endpoint wizard step 3 (#155) ───────────────────────────

// Two-tab install command view: the default `curl|bash`-shaped one-liner
// plus a "Full script" tab that fetches the rendered installer body for
// security-conscious customers (SOC-2 reviews, MSP fleets) who refuse to
// pipe-to-bash unread. The render route is single-use, so fetching the
// full script burns the token — when that happens, the one-liner tab
// flips to a "spent" state so an operator who clicks back to it gets a
// clear "regenerate" CTA rather than a curl that 410s in their terminal.
//
// Discriminated-union state — exhaustive switch in FullScriptPanel makes
// adding a new variant a type error rather than a silent fall-through.
// `loading` is the seed: the Full-script panel only mounts when the user
// selects that tab, and by the time it mounts a fetch is in flight, so
// there is no "never fetched yet" state to model.
//
// `canRetry` on error: true ⇒ pre-server-response failure (CORS, offline,
// DNS); token nonce is intact server-side so the user can retry. false ⇒
// HTTP-level error (5xx, 401, etc.); the request reached the server so
// the token may already be consumed — treat as token-spent for the
// one-liner panel and don't offer retry (a re-fetch would 410).
type FullScriptState =
  | { kind: "loading" }
  | { kind: "ready"; body: string }
  | { kind: "spent" }
  | { kind: "error"; message: string; canRetry: boolean };

function InstallCommandTabs({
  os,
  install,
  oneLiner,
  onRegenerate,
  isRegenerating,
}: {
  os: AgentOSType;
  install: AgentKeyInstall;
  oneLiner: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}) {
  const [tab, setTab] = useState<"oneliner" | "fullscript">("oneliner");
  const [script, setScript] = useState<FullScriptState>({ kind: "loading" });
  // Synchronous guard against concurrent fetches: two rapid clicks on
  // the Full script tab before React commits the kind→"loading"
  // transition could both fire fetches without this ref. It's set on
  // fetch start and cleared on transient-network-error so startFetch
  // (also passed down as the Retry-button onClick) can run again.
  const fetchedRef = useRef(false);
  const mountedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const headingId = useId();

  useEffect(() => {
    // Initialize on every mount — StrictMode runs setup→cleanup→setup
    // in dev, so a one-time initializer outside the effect would leave
    // mountedRef stuck false after the StrictMode cleanup and every
    // setState inside fetchFullScript would early-return.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // The token nonce is consumed once the request actually reaches the
  // server. That covers ready, spent (410), AND HTTP-level errors
  // (5xx/401 — represented as kind=error with canRetry=false). A
  // pre-server-response failure (canRetry=true) leaves the token
  // intact, so we keep the one-liner usable in that branch only.
  const tokenSpent =
    script.kind === "ready" ||
    script.kind === "spent" ||
    (script.kind === "error" && !script.canRetry);

  async function fetchFullScript() {
    const controller = new AbortController();
    abortRef.current = controller;
    // Tracks whether the server returned response headers. Anything
    // after this point — including a `.text()` body-stream read that
    // throws mid-stream — means the request reached the server and
    // the token nonce was (or may have been) consumed. Without this
    // flag, a TCP reset during body decode would land in the network-
    // error catch with canRetry=true and Retry would 410.
    let headersReceived = false;
    try {
      // Token is a bearer credential — keep it out of the disk cache,
      // out of Referer headers, and don't ship cookies the route can
      // ignore. Defense-in-depth: backend should also send no-store,
      // but a one-line client posture isn't waiting on backend changes.
      const resp = await fetch(install.install_url, {
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
      });
      headersReceived = true;
      if (!mountedRef.current) return;
      if (resp.status === 410) {
        setScript({ kind: "spent" });
        return;
      }
      if (!resp.ok) {
        // HTTP-level error: server responded so the token nonce may
        // have been consumed. canRetry=false flips the one-liner to
        // its "spent" state and suppresses the Retry button — a
        // re-fetch would 410 and double-confuse the operator.
        setScript({
          kind: "error",
          message: `The server returned an error (HTTP ${resp.status}).`,
          canRetry: false,
        });
        return;
      }
      const body = await resp.text();
      if (!mountedRef.current) return;
      setScript({ kind: "ready", body });
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (headersReceived) {
        // Body-stream failure on a 2xx: the request hit the server
        // and the nonce is gone. Treat as token-consumed.
        setScript({
          kind: "error",
          message: "The server returned an unreadable response.",
          canRetry: false,
        });
        return;
      }
      // Pre-server-response failure (CORS, offline, DNS). Token nonce
      // is intact server-side, so allow retry: clear the in-flight
      // guard and surface a Retry affordance in the error panel.
      // Deliberately fixed message — some browsers include the failed
      // URL in `TypeError: Failed to fetch <url>`, which would put the
      // bearer token straight into the DOM and any front-end error
      // analytics that vacuum innerText.
      fetchedRef.current = false;
      setScript({
        kind: "error",
        message: "Network error — couldn't reach the server.",
        canRetry: true,
      });
    }
  }

  function startFetch() {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setScript({ kind: "loading" });
    void fetchFullScript();
  }

  function handleTabChange(value: string) {
    const next = value === "fullscript" ? "fullscript" : "oneliner";
    setTab(next);
    if (next === "fullscript") startFetch();
  }

  const singleUseHint =
    "This command works once — if it fails for any reason, regenerate a new install command from the dashboard before retrying.";
  const runHint =
    (os === "linux" || os === "macos"
      ? "Paste this into a terminal as a user with sudo access. "
      : "Paste this into an elevated PowerShell prompt (Run as Administrator). ") + singleUseHint;
  const runHintAfterReview =
    (os === "linux" || os === "macos"
      ? "Review the script, then paste it into a terminal as a user with sudo access. "
      : "Review the script, then paste it into an elevated PowerShell prompt (Run as Administrator). ") +
    singleUseHint;

  return (
    <div className="space-y-2">
      <h3 id={headingId} className="text-sm font-medium">
        Run on the target host
      </h3>

      <Tabs value={tab} onValueChange={handleTabChange} className="space-y-2">
        <TabsList
          aria-labelledby={headingId}
          className="inline-flex h-9 items-center justify-center rounded-md border bg-muted/30 p-0.5 text-xs"
        >
          <TabsTrigger
            value="oneliner"
            data-testid="tab-oneliner"
            className="rounded px-3 py-1 text-xs data-[state=active]:bg-background data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          >
            One-liner
          </TabsTrigger>
          <TabsTrigger
            value="fullscript"
            data-testid="tab-fullscript"
            className="rounded px-3 py-1 text-xs data-[state=active]:bg-background data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-sm"
          >
            Full script
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oneliner" className="space-y-2">
          {tokenSpent ? (
            <TokenSpentNotice
              testId="oneliner-spent"
              message={
                onRegenerate
                  ? "This install token has been spent — use the Regenerate button below to get a fresh install command."
                  : "This install token has been spent. Regenerate a new install command from the dashboard to deploy this agent."
              }
              onRegenerate={onRegenerate}
              isRegenerating={isRegenerating}
            />
          ) : (
            <>
              <p className="text-xs text-muted-foreground">{runHint}</p>
              <div className="relative rounded-md border bg-muted">
                <div className="absolute right-2 top-2">
                  <CopyBtn text={oneLiner} />
                </div>
                <pre
                  data-testid="install-one-liner"
                  className="overflow-x-auto p-4 pr-10 font-mono text-xs leading-relaxed"
                >
                  {oneLiner}
                </pre>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="fullscript" className="space-y-2">
          <FullScriptPanel
            script={script}
            runHintAfterReview={runHintAfterReview}
            onRetry={startFetch}
            onRegenerate={onRegenerate}
            isRegenerating={isRegenerating}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Shared amber notice rendered when the install token has been consumed
// — either from the One-liner panel's view (after the Full-script tab
// burned the nonce) or the Full-script panel's view (after a 410 or an
// HTTP-error response). Single source for the copy and styling so they
// can't drift apart on a future tweak.
function TokenSpentNotice({
  testId,
  message,
  onRegenerate,
  isRegenerating,
}: {
  testId: string;
  message: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400"
    >
      <p role="alert">{message}</p>
      {onRegenerate && (
        <div className="mt-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid={`${testId}-regenerate`}
            onClick={onRegenerate}
            disabled={isRegenerating}
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200"
          >
            {isRegenerating ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            )}
            Regenerate
          </Button>
        </div>
      )}
    </div>
  );
}

function FullScriptPanel({
  script,
  runHintAfterReview,
  onRetry,
  onRegenerate,
  isRegenerating,
}: {
  script: FullScriptState;
  runHintAfterReview: string;
  onRetry: () => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}) {
  // Exhaustiveness enforced by TypeScript via the discriminated union;
  // no runtime throw — a render-time throw would crash the surrounding
  // dialog and there's no error boundary on this tree.
  if (script.kind === "loading") {
    return (
      <div
        className="flex items-center gap-2 rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground"
        data-testid="fullscript-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Fetching install script&hellip;
      </div>
    );
  }
  if (script.kind === "spent") {
    return (
      <TokenSpentNotice
        testId="fullscript-spent"
        message={
          onRegenerate
            ? "This install token has already been spent — use the Regenerate button below to get a fresh install command."
            : "This install token has already been spent. Regenerate a new install command from the dashboard to view or run the script."
        }
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
    );
  }
  if (script.kind === "error") {
    // Retry is only safe for canRetry=true (pre-server-response
    // failures, token intact). For HTTP-level errors the token may
    // have already been consumed, so re-fetching would 410. In that
    // case the parent's `tokenSpent` derivation flips the one-liner
    // panel to a "regenerate" CTA — this panel's copy stays focused
    // on the diagnostic message.
    //
    // role="alert" sits on the message paragraph, not the surrounding
    // container — wrapping interactive content (the Retry button) in
    // a live region causes some screen readers to re-announce the
    // error every time the button receives focus.
    return (
      <div
        data-testid="fullscript-error"
        className="space-y-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400"
      >
        <p role="alert">
          Couldn&apos;t fetch the install script: {script.message}
        </p>
        {script.canRetry ? (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="fullscript-retry"
              onClick={onRetry}
              className="border-red-500/50 text-red-300 hover:bg-red-500/20 hover:text-red-200"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </Button>
            <span className="text-xs text-red-400/80">
              Or regenerate from the dashboard if the connection problem
              persists.
            </span>
          </div>
        ) : (
          <div
            data-testid="fullscript-error-regenerate"
            className="flex flex-wrap items-center gap-3"
          >
            <p className="text-xs text-red-400/80">
              {onRegenerate
                ? "The previous token may have already been consumed — use Regenerate to get a fresh install command."
                : "Regenerate a new install command from the dashboard — the previous token may have already been consumed."}
            </p>
            {onRegenerate && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="fullscript-error-regenerate-btn"
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="border-red-500/50 text-red-300 hover:bg-red-500/20 hover:text-red-200"
              >
                {isRegenerating ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                )}
                Regenerate
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
  // kind === "ready" — discriminated union narrows `script.body` safely.
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{runHintAfterReview}</p>
      <div className="relative rounded-md border bg-muted">
        <div className="absolute right-2 top-2">
          <CopyBtn text={script.body} />
        </div>
        <pre
          data-testid="install-full-script"
          className="max-h-80 overflow-auto p-4 pr-10 font-mono text-xs leading-relaxed"
        >
          {script.body}
        </pre>
      </div>
    </div>
  );
}

function EndpointDeployNotes({ os }: { os: AgentOSType }) {
  if (os === "windows") {
    return (
      <div className="rounded-md border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">If the Windows agent does not appear:</p>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          <li>The command should be run from an elevated PowerShell prompt.</li>
          <li>The NoExit wrapper keeps the window open so you can copy installer errors.</li>
          <li>Check Services for Fluent Bit and PrayingMantisAgentUpdater, then click Refresh.</li>
        </ul>
      </div>
    );
  }
  if (os === "macos") {
    return (
      <div className="rounded-md border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">macOS prerequisites:</p>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          <li>Install Homebrew first as the regular user, not from a root shell.</li>
          <li>The installer uses Homebrew for Fluent Bit and Python 3.12.</li>
          <li>After install, grant Full Disk Access so system logs can be read.</li>
        </ul>
      </div>
    );
  }
  return (
    <div className="rounded-md border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
      Run the command on the target host, then use Refresh to confirm the first heartbeat.
    </div>
  );
}

function StepEndpointDeploy({
  install,
  os,
  error,
  agentCount,
  pollCapReached,
  onRefreshAgents,
  onDone,
  onRegenerate,
  isRegenerating,
}: {
  install: AgentKeyInstall | null;
  os: AgentOSType;
  error: string | null;
  agentCount: number;
  pollCapReached: boolean;
  onRefreshAgents: () => void;
  onDone: () => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}) {
  if (!install) {
    // Mint failed (display the error from the parent) — show a clear
    // CTA back to step 2 instead of a blank panel.
    return (
      <div className="space-y-4">
        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400"
          >
            {error}
          </div>
        ) : (
          <p className="rounded-md bg-muted/50 px-3 py-3 text-sm text-muted-foreground">
            Install URL was not generated. Go back and try again.
          </p>
        )}
        <div className="flex justify-end">
          <Button
            onClick={onDone}
            variant="secondary"
            data-testid="install-fail-close"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  const oneLiner = buildOneLiner(os, install.install_url);

  return (
    <div className="space-y-5">
      {/* Success */}
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3">
        <p className="text-sm font-medium text-green-400">
          Install command ready for{" "}
          <span className="font-semibold">{install.instance_label}</span>.
        </p>
        <p className="mt-1 text-xs text-green-400/80">
          {formatExpiresAt(install.expires_at)}. The token is consumed on
          first run, so don&apos;t share it.
        </p>
      </div>

      {/* Error (e.g. backend partial failure) */}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400"
        >
          {error}
        </div>
      )}

      {/* Install Command */}
      <InstallCommandTabs
        key={install.token}
        os={os}
        install={install}
        oneLiner={oneLiner}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
      <EndpointDeployNotes os={os} />
      {os === "macos" && <MacOsFdaCallout />}

      {/* Status panel */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="flex items-center gap-3">
          {agentCount > 0 ? (
            <>
              <span
                className="inline-flex h-2 w-2 rounded-full bg-green-500"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-green-500">
                Agent active
              </span>
              <span className="text-sm text-muted-foreground">
                ({agentCount} connected)
              </span>
            </>
          ) : pollCapReached ? (
            <span className="text-sm text-muted-foreground">
              Stopped checking after 5 minutes &mdash; click Refresh to retry.
            </span>
          ) : (
            <>
              <Loader2
                className="h-3.5 w-3.5 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
              <span className="text-sm text-muted-foreground">
                Waiting for first heartbeat&hellip;
              </span>
            </>
          )}
          <button
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={onRefreshAgents}
            aria-label="Refresh agent status"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </button>
        </div>
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
